/**
 * ServiceChargesPage
 *
 * Staff queue for one-time service fees. The important column is the expiry:
 * an authorization is a hold with a deadline, typically 7 days, and if it
 * lapses before capture the funds are released and the customer must confirm
 * again. Holds nearing expiry sort to the top.
 *
 * Capture means "the work is done, collect the money" — it should never be
 * clicked before the service has actually been delivered.
 */

import { useEffect, useState } from 'react';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import {
  listServiceCharges,
  createServiceCharge,
  captureServiceCharge,
  voidServiceCharge,
  formatAmount,
  hoursUntilExpiry,
  SERVICE_PRESETS,
  type ServiceCharge,
  type ServiceChargeStatus,
} from '../api/service-charges';
import { formatRelativeTime } from '../utils/formatters';

const statusVariants: Record<ServiceChargeStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  authorized: 'info',
  delivered: 'info',
  captured: 'success',
  expired: 'warning',
  voided: 'default',
  failed: 'error',
  refunded: 'default',
};

export function ServiceChargesPage() {
  const [charges, setCharges] = useState<ServiceCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<ServiceChargeStatus | 'all'>('authorized');
  const [showForm, setShowForm] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setCharges(await listServiceCharges(filter === 'all' ? undefined : filter));
      setError(null);
    } catch (err) {
      setError('Failed to load service charges');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleCapture = async (charge: ServiceCharge) => {
    const who = window.prompt(
      `Capture ${formatAmount(charge.amount_cents, charge.currency)} for "${charge.description}"?\n\n` +
        'Only do this if the work has actually been delivered.\n\n' +
        'Your name or initials:'
    );
    if (!who) return;

    setBusy(charge.id);
    try {
      await captureServiceCharge(charge.id, who);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Capture failed');
    } finally {
      setBusy(null);
    }
  };

  const handleVoid = async (charge: ServiceCharge) => {
    const reason = window.prompt(
      `Release the hold on "${charge.description}"?\n\n` +
        'The customer will not be charged.\n\nReason:'
    );
    if (!reason) return;

    setBusy(charge.id);
    try {
      await voidServiceCharge(charge.id, reason);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Void failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer
      title="Service Charges"
      subtitle="One-time fees. Capture only once the work is delivered."
      action={
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'New charge'}
        </Button>
      }
    >
      {showForm && (
        <CreateChargeForm
          onCreated={(message) => {
            setNotice(message);
            setShowForm(false);
            setFilter('draft');
          }}
          onError={setError}
        />
      )}

      <div className="mb-4 flex gap-2">
        {(['authorized', 'draft', 'captured', 'expired', 'all'] as const).map((option) => (
          <Button
            key={option}
            variant={filter === option ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter(option)}
          >
            {option === 'all' ? 'All' : option.charAt(0).toUpperCase() + option.slice(1)}
          </Button>
        ))}
      </div>

      {notice && (
        <Card className="mb-4 border-blue-200 bg-blue-50">
          <p className="text-sm text-blue-800">{notice}</p>
        </Card>
      )}

      {error && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : charges.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No service charges with this status.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {charges.map((charge) => (
            <ChargeRow
              key={charge.id}
              charge={charge}
              busy={busy === charge.id}
              onCapture={() => handleCapture(charge)}
              onVoid={() => handleVoid(charge)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

/**
 * Create a charge for a customer.
 *
 * Staff create it; the customer confirms it in the portal. That confirmation is
 * what places the hold — we never authorize on their behalf, both because their
 * click is the authorization record and because a customer-initiated
 * transaction earns the longer hold window.
 */
function CreateChargeForm({
  onCreated,
  onError,
}: {
  onCreated: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [customerId, setCustomerId] = useState('');
  const [preset, setPreset] = useState<string>(SERVICE_PRESETS[0].type);
  // Explicit <string>: SERVICE_PRESETS is `as const`, so inference would pin
  // these to the first preset's literal type and reject every other preset.
  const [description, setDescription] = useState<string>(SERVICE_PRESETS[0].label);
  const [amount, setAmount] = useState<string>(String(SERVICE_PRESETS[0].amountCents / 100));
  const [submitting, setSubmitting] = useState(false);

  const applyPreset = (type: string) => {
    const found = SERVICE_PRESETS.find((p) => p.type === type);
    setPreset(type);
    if (found) {
      setDescription(found.label);
      setAmount(String(found.amountCents / 100));
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      onError('Enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createServiceCharge({
        customer_id: customerId.trim(),
        service_type: preset,
        description: description.trim(),
        // Rounded explicitly: 149.99 * 100 is 14998.999… in floating point.
        amount_cents: Math.round(dollars * 100),
      });

      const parts = [`Created ${formatAmount(result.data.amount_cents)} charge.`];
      parts.push(
        result.notified
          ? 'Customer notified via GHL.'
          : `NOT notified — ${result.notify_error ?? 'unknown reason'}. Follow up manually.`
      );
      if (result.warning) parts.push(result.warning);

      onCreated(parts.join(' '));
      setCustomerId('');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create charge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-4">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer ID</label>
          <input
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            required
            placeholder="cust_…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
          <select
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {SERVICE_PRESETS.map((p) => (
              <option key={p.type} value={p.type}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="font-normal text-gray-500">(the customer sees this)</span>
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (USD)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <p className="text-xs text-gray-500">
          The customer confirms this in their portal, which places a hold on their card. Nothing is
          collected until you capture it after delivering the work.
        </p>

        <Button type="submit" loading={submitting}>
          Create &amp; notify
        </Button>
      </form>
    </Card>
  );
}

function ChargeRow({
  charge,
  busy,
  onCapture,
  onVoid,
}: {
  charge: ServiceCharge;
  busy: boolean;
  onCapture: () => void;
  onVoid: () => void;
}) {
  const hoursLeft = hoursUntilExpiry(charge.authorization_expires_at);
  const urgent = charge.status === 'authorized' && hoursLeft !== null && hoursLeft < 48;

  return (
    <Card className={urgent ? 'border-amber-300' : undefined}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{charge.description}</p>
            <Badge variant={statusVariants[charge.status]}>{charge.status}</Badge>
          </div>

          <p className="mt-1 text-sm text-gray-600">
            {formatAmount(charge.amount_cents, charge.currency)} · {charge.service_type}
          </p>

          <p className="mt-1 text-xs text-gray-500">
            Customer {charge.customer_id} · created {formatRelativeTime(charge.created_at)}
          </p>

          {charge.status === 'authorized' && hoursLeft !== null && (
            <p className={`mt-2 text-sm ${urgent ? 'font-medium text-amber-700' : 'text-gray-600'}`}>
              {hoursLeft <= 0
                ? 'Hold has expired — funds released'
                : hoursLeft < 48
                  ? `Expires in ${Math.floor(hoursLeft)}h — capture or it releases`
                  : `Expires in ${Math.floor(hoursLeft / 24)} days`}
            </p>
          )}

          {charge.failure_reason && (
            <p className="mt-2 text-sm text-red-700">{charge.failure_reason}</p>
          )}

          {charge.status === 'captured' && charge.delivered_by && (
            <p className="mt-2 text-xs text-gray-500">
              Delivered by {charge.delivered_by}
              {charge.captured_at && ` · captured ${formatRelativeTime(charge.captured_at)}`}
            </p>
          )}
        </div>

        {charge.status === 'authorized' && (
          <div className="flex shrink-0 gap-2">
            <Button size="sm" onClick={onCapture} loading={busy}>
              Mark delivered &amp; capture
            </Button>
            <Button size="sm" variant="ghost" onClick={onVoid} disabled={busy}>
              Release
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
